import { init, replayIntegration } from '@sentry/browser';

// Helper function to detect browser extension errors
const isExtensionError = event => event.exception?.values?.[0]?.stacktrace?.frames?.some(
  frame =>
    frame.filename?.includes('extension://') ||
    frame.filename?.includes('moz-extension://') ||
    frame.filename?.includes('safari-extension://') ||
    frame.filename?.includes('chrome-extension://') ||
    frame.filename?.includes('iabjs://') // Facebook IAB injected scripts (Android WebView)
);

// Helper function to determine if we should ignore network errors
const shouldIgnoreNetworkError = error => {
  const status = error.response?.status;
  const url = error.config?.url || '';
  const method = error.config?.method || '';

  // Always ignore 404s
  if (status === 404) {
    return true;
  }

  // Ignore expected auth errors
  if (
    [400, 401, 403].includes(status) &&
    (url.includes('/auth/') ||
      url.includes('/login/') ||
      url.includes('/oauth/') ||
      url.includes('/signup/'))
  ) {
    return true;
  }

  // Ignore timeouts on known slow endpoints
  if (
    error.code === 'ECONNABORTED' &&
    (url.includes('/upload/') || url.includes('/export/') || url.includes('/download/'))
  ) {
    return true;
  }

  // Ignore client errors (4xx) or any OPTIONS request
  if ((status >= 400 && status < 500) || method === 'OPTIONS') {
    return true;
  }

  return false;
};

// Helper function to detect noisy errors
const isNoiseError = (error, event) => {
  const errorMessage = error?.message || event.message || '';

  const noisePatterns = [
    /network error/i,
    /timeout/i,
    /aborted/i,
    /cancelled/i,
    /script error/i,
    /non-error promise rejection/i,
    /resizeobserver loop limit exceeded/i,
    /chunkloaderror/i,
    /loading chunk \d+ failed/i,
    /loading css chunk \d+ failed/i,
  ];

  return noisePatterns.some(pattern => pattern.test(errorMessage));
};

// Sentry configuration for production environment
export const initSentry = () => {
  const DSN =
    import.meta.env.VITE_SENTRY_DSN ||
    import.meta.env.REACT_APP_SENTRY_DSN ||
    'https://e0e474a0fab14f919147bc25cd32429a@o38629.ingest.us.sentry.io/1341698';
  const ENV =
    import.meta.env.VITE_ENV ||
    import.meta.env.REACT_APP_ENV ||
    import.meta.env.MODE ||
    'production';
  const RELEASE =
    import.meta.env.VITE_VERSION ||
    import.meta.env.REACT_APP_VERSION ||
    'unknown';
  const DIST =
    import.meta.env.VITE_BUILD ||
    import.meta.env.REACT_APP_BUILD;

  if (!DSN) {
    // eslint-disable-next-line no-console
    console.warn('Sentry DSN not set. Sentry will not be initialized.');
    return;
  }
  if (!ENV) {
    // eslint-disable-next-line no-console
    console.warn('Sentry ENV not set. Sentry will not be initialized.');
    return;
  }

  // Solo habilitar en producción o staging
  if (!['production', 'staging'].includes(ENV)) {
    // eslint-disable-next-line no-console
    console.log(`Sentry disabled for env: ${ENV}`);
    return;
  }

  init({
    dsn: DSN,
    environment: ENV,
    release: RELEASE,
    dist: DIST,
    integrations: [
      replayIntegration({
        networkDetailAllowUrls: [window.location.origin],
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Increase serialization depth so nested objects appear fully in Sentry extra data
    // instead of being truncated as [Object] (default is 3)
    normalizeDepth: 10,

    // Performance monitoring
    tracesSampleRate: 0.1, // 10% of transactions
    replaysSessionSampleRate: 0.05, // 5% of sessions
    replaysOnErrorSampleRate: 0.2, // 20% of error sessions

    // Advanced filtering to reduce noise
    beforeSend(event, hint) {
      const error = hint.originalException;

      // Skip errors from browser extensions
      if (isExtensionError(event)) {
        return null;
      }

      // Skip network errors we don't care about
      if (error?.isAxiosError && shouldIgnoreNetworkError(error)) {
        return null;
      }

      // Skip known noisy errors
      if (isNoiseError(error, event)) {
        return null;
      }

      // Skip Sentry own errors (rate limit, auth, etc)
      const sentryUrl = event.request?.url || error?.config?.url || '';
      if (sentryUrl.includes('sentry.io')) {
        return null;
      }
      // Skip Sentry HTTP errors
      const sentryStatus = error?.response?.status;
      if (
        sentryUrl.includes('sentry.io') &&
        [429, 403, 401, 400, 404].includes(sentryStatus)
      ) {
        return null;
      }
      if (sentryUrl.includes('sentry.io') && sentryStatus >= 500) {
        return null;
      }

      // Add additional context for debugging
      if (error?.isAxiosError) {
        event.tags = {
          ...event.tags,
          errorType: 'network',
          httpStatus: error.response?.status,
          httpMethod: error.config?.method?.toUpperCase(),
        };

        event.extra = {
          ...event.extra,
          url: error.config?.url,
          requestData: error.config?.data,
        };
      }

      return event;
    },

    // Ignore specific error patterns
    ignoreErrors: [
      // Facebook in-app browser injects scripts that call native bridges unavailable in its sandboxed WebView
      // Android: Java bridge destroyed on beforeunload (REACT-5RQ)
      // iOS: WKWebView bridge doesn't exist in Facebook IAB (REACT-5TE)
      'Error invoking postMessage',
      'window.webkit.messageHandlers',

      // Browser extension errors
      'top.GLOBALS',
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'atomicFindClose',
      'fb_xd_fragment',
      'bmi_SafeAddOnload',
      'EBCallBackMessageReceived',
      'conduitPage',

      // Common browser/network errors
      'Network Error',
      'NetworkError',
      'Failed to fetch',
      'Load failed',
      'Script error',
      'Non-Error promise rejection captured',
      'ResizeObserver loop limit exceeded',
      'ChunkLoadError',
      'Loading chunk',
      'Loading CSS chunk',

      // Common React errors that aren't actionable
      'Warning: ',
      'Minified React error',
    ],

    // Set default tags
    initialScope: {
      tags: {
        component: 'b4b-react',
        version: RELEASE,
      },
    },
  });
  // eslint-disable-next-line no-console
  console.log(`Sentry initialized for ${ENV}`);
};

// Status codes worth reporting as bugs; everything else (4xx, network errors) is expected/noise
export const REPORTABLE_STATUS_CODES = [500, 502, 503, 504, 401, 403];

const PII_FIELDS = new Set(['phone_number', 'phone', 'email', 'password', 'first_name', 'last_name']);

export const sanitizeForSentry = obj => {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj;
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => {
      if (PII_FIELDS.has(key)) return [key, '[REDACTED]'];
      if (value && typeof value === 'object') return [key, sanitizeForSentry(value)];
      return [key, value];
    })
  );
};

export default initSentry;
